import React, { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiJson } from "@/lib/api";
import { apiRequest } from "@/lib/queryClient";
import { dbg } from "@/lib/dbg";
import { FlightStatusBadge, type FlightStatus } from "@/components/campaigns/FlightStatusBadge";

// Slim campaign summary type - optimized for header display
type CampaignSummary = {
  id: number;
  name: string;
  description?: string | null;
  status?: string | null;
  statusId?: number | null;
  createdAt?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  retailerId?: number | null;
  cycleId?: number | null;
  advertiserId?: number | null;
  advertiserName?: string | null; // Direct from summary endpoint
  snapshotId?: number | null;
  snapshotSource?: "snapshot" | "locked_cycle" | "none" | null;
  totalPacks: number;
  totalStores: number;
  totalOperators: number;
  totalFormats: number;
  // Attribution fields for reassignment tracking
  lastUpdatedBy?: number | null;
  lastUpdatedByName?: string | null;
  lastUpdateReason?: string | null; // 'MANUAL' | 'REASSIGNMENT'
  lastUpdatedAt?: string | null;
  // Booking Context (Phase 4)
  bookingId?: number | null;
  bookingLinkSource?: string | null;
  bookingName?: string | null;
  bookingStatus?: string | null;
  flightStatus?: FlightStatus;
};

function formatDate(dateString?: string | null): string {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

function getSnapshotDisplay(summary: CampaignSummary): { value: string; hint: string } {
  if (typeof summary.snapshotId === "number") {
    return {
      value: String(summary.snapshotId),
      hint: "Active snapshot",
    };
  }

  if (summary.snapshotSource === "locked_cycle") {
    return {
      value: "Missing",
      hint: "Viewing frozen cycle data",
    };
  }

  return {
    value: "Missing",
    hint: "No saved snapshot found",
  };
}

// Map status name to badge variant
function getStatusBadgeVariant(status: string): "success" | "warning" | "info" | "secondary" | "destructive" | "default" {
  const statusLower = status.toLowerCase();
  switch (statusLower) {
    case "confirmed":
    case "active":
    case "posted":
      return "success";
    case "draft":
      return "secondary";
    case "artwork_received":
    case "artwork_approved":
    case "printed":
      return "info";
    case "removed":
    case "completed":
      return "destructive";
    default:
      return "default";
  }
}

interface CampaignHeaderProps {
  campaignId: number;
  /** Hide the card wrapper - used when only needing data for external display */
  hideCard?: boolean;
  /** Callback to provide header data to parent component */
  onDataLoaded?: (data: {
    name: string;
    status: string;
    statusBadgeVariant: "success" | "warning" | "info" | "secondary" | "destructive" | "default";
    retailerName: string;
    cycleDisplay: string;
    dateRange: string;
  }) => void;
}

export function CampaignHeader({ campaignId, hideCard = false, onDataLoaded }: CampaignHeaderProps) {
  const queryClient = useQueryClient();
  const [pendingStatusId, setPendingStatusId] = React.useState<number | null>(null);

  // OPTIMIZED: Use new slim /api/campaigns/:id/summary endpoint
  const { data: campaignSummary, isLoading, error } = useQuery<CampaignSummary>({
    queryKey: ["/api/campaigns", campaignId, "summary"],
    queryFn: async () => apiJson(`/api/campaigns/${campaignId}/summary`),
    enabled: !!campaignId,
    staleTime: 30_000,
    refetchOnMount: 'always', // Override global setting to ensure fresh data on page load
  });

  // Extract counts directly from slim response
  const totalStores = campaignSummary?.totalStores ?? 0;
  const totalOperators = campaignSummary?.totalOperators ?? 0;
  const totalFormats = campaignSummary?.totalFormats ?? 0;

  // Fetch retailers for name lookup
  const shouldFetchRetailers = !!campaignSummary && typeof campaignSummary.retailerId === "number";
  const { data: allRetailers = [] } = useQuery<any[]>({
    queryKey: ["/api/retailers"],
    queryFn: async () => apiJson("/api/retailers"),
    enabled: shouldFetchRetailers,
    staleTime: 60_000,
  });

  // Fetch booking cycles to get cycle names and dates
  const { data: bookingCyclesResponse } = useQuery<{ data: any[]; totalCount: number } | any[]>({
    queryKey: ["/api/booking-cycles"],
    queryFn: async () => apiJson("/api/booking-cycles"),
    staleTime: 60_000,
  });

  // Campaign statuses for dropdown
  const { data: campaignStatuses = [] } = useQuery<any[]>({
    queryKey: ["/api/campaign-statuses"],
    queryFn: async () => apiJson("/api/campaign-statuses"),
    staleTime: 60_000,
  });

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (statusId: number) => {
      setPendingStatusId(statusId);
      return apiRequest(`/api/campaigns/${campaignId}/status`, "PATCH", { statusId });
    },
    onSuccess: async () => {
      // Invalidate both the summary AND the full campaign detail
      await queryClient.invalidateQueries({ queryKey: ["/api/campaigns", campaignId, "summary"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/campaigns", campaignId] });
      // Don't await - let the full campaigns list refresh in background
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      setPendingStatusId(null);
    },
    onError: () => {
      setPendingStatusId(null);
    },
  });

  // Handle both paginated and legacy response formats
  const bookingCycles = useMemo(() => {
    if (!bookingCyclesResponse) return [];
    if (Array.isArray(bookingCyclesResponse)) return bookingCyclesResponse;
    if (bookingCyclesResponse.data && Array.isArray(bookingCyclesResponse.data))
      return bookingCyclesResponse.data;
    return [];
  }, [bookingCyclesResponse]);

  React.useEffect(() => {
    if (campaignSummary) {
      dbg.log("[CAMPAIGN_HEADER] Using optimized summary endpoint", {
        id: campaignSummary.id,
        status: campaignSummary.status ?? null,
        totalStores: campaignSummary.totalStores,
        totalOperators: campaignSummary.totalOperators,
        totalFormats: campaignSummary.totalFormats,
      });
    }
  }, [campaignSummary]);

  // Get current status ID
  const currentStatusId: number | undefined = useMemo(() => {
    if (pendingStatusId) return pendingStatusId;
    if (campaignSummary?.statusId) return campaignSummary.statusId as number;
    // Fallback: map status name to statusId
    const byName = campaignStatuses.find(
      (s) => String(s.name).toLowerCase() === String(campaignSummary?.status || "").toLowerCase()
    );
    return byName?.id;
  }, [pendingStatusId, campaignSummary?.statusId, campaignSummary?.status, campaignStatuses]);

  // Computed values
  const statusName = campaignSummary?.status || "Unknown";

  const retailerName = useMemo(() => {
    if (typeof campaignSummary?.retailerId === "number" && allRetailers.length > 0) {
      const found = allRetailers.find((r: any) => r.id === campaignSummary.retailerId);
      if (found?.name) return found.name;
    }
    return "—";
  }, [campaignSummary?.retailerId, allRetailers]);

  // Use advertiserName directly from summary (no lookup needed)
  const advertiserName = campaignSummary?.advertiserName || "—";
  const snapshotDisplay = campaignSummary ? getSnapshotDisplay(campaignSummary) : { value: "—", hint: "" };

  const cycleDisplay = useMemo(() => {
    if (typeof campaignSummary?.cycleId === "number" && bookingCycles.length > 0) {
      const cycle = bookingCycles.find((c: any) => c.id === campaignSummary.cycleId);
      if (cycle?.name) return cycle.name;
    }
    if (typeof campaignSummary?.cycleId === "number") {
      return `Cycle ${campaignSummary.cycleId}`;
    }
    return "—";
  }, [campaignSummary?.cycleId, bookingCycles]);

  const dateRange = useMemo(() => {
    if (typeof campaignSummary?.cycleId === "number" && bookingCycles.length > 0) {
      const cycle = bookingCycles.find((c: any) => c.id === campaignSummary.cycleId);
      if (cycle?.startDate && cycle?.endDate) {
        const formatDateShort = (date: Date) => {
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const day = date.getDate().toString().padStart(2, "0");
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        };
        return `${formatDateShort(new Date(cycle.startDate))} – ${formatDateShort(new Date(cycle.endDate))}`;
      }
    }
    return "—";
  }, [campaignSummary?.cycleId, bookingCycles]);

  // Notify parent of data when available
  React.useEffect(() => {
    if (campaignSummary && onDataLoaded) {
      onDataLoaded({
        name: campaignSummary.name,
        status: statusName,
        statusBadgeVariant: getStatusBadgeVariant(statusName),
        retailerName,
        cycleDisplay,
        dateRange,
      });
    }
  }, [campaignSummary, statusName, retailerName, cycleDisplay, dateRange, onDataLoaded]);

  if (hideCard) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Basic Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading campaign details…</div>
        </CardContent>
      </Card>
    );
  }

  if (error || !campaignSummary) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Basic Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-600">Failed to load campaign details.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Basic Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-3">
          {/* Left Column */}
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Campaign ID:</span>
              <span className="font-medium">{campaignSummary.id}</span>
            </div>
            {/* Booking Context - Phase 4 */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Booking Plan:</span>
              <span className="font-medium text-right">
                {campaignSummary.bookingId ? (
                  <span className="flex items-center gap-1.5">
                    <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal border-blue-200 text-blue-700 bg-blue-50">
                      Linked
                    </Badge>
                    <span className="text-sm truncate max-w-[150px]" title={campaignSummary.bookingName || ""}>
                      {campaignSummary.bookingName || `Booking #${campaignSummary.bookingId}`}
                    </span>
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground italic flex items-center gap-1" title="Created directly in Campaigns">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                    Manual Campaign
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Date Created:</span>
              <span className="font-medium">{formatDate(campaignSummary.createdAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Total Stores:</span>
              <span className="font-medium">{totalStores}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Operators:</span>
              <span className="font-medium">{totalOperators}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Media Formats:</span>
              <span className="font-medium">{totalFormats}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Account:</span>
              <span className="font-medium">{advertiserName}</span>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Select
                value={currentStatusId ? String(currentStatusId) : ""}
                onValueChange={(val) => {
                  const statusId = parseInt(val);
                  if (!isNaN(statusId)) updateStatusMutation.mutate(statusId);
                }}
                disabled={updateStatusMutation.isPending || campaignStatuses.length === 0}
              >
                <SelectTrigger className="w-[160px] h-8 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{
                        backgroundColor:
                          campaignStatuses.find(
                            (s) => s.name?.toLowerCase?.() === statusName?.toLowerCase()
                          )?.color || "#6B7280",
                      }}
                    />
                    <SelectValue placeholder="Select status..." />
                  </div>
                </SelectTrigger>
                <SelectContent className="z-50 bg-white text-gray-900 border border-gray-200 shadow-lg rounded-md max-h-64 overflow-auto">
                  {campaignStatuses.map((s) => (
                    <SelectItem
                      key={s.id}
                      value={String(s.id)}
                      className="cursor-pointer text-gray-900 focus:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: s.color || "#6B7280" }}
                        />
                        {s.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {campaignSummary.flightStatus && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">In Field:</span>
                <FlightStatusBadge status={campaignSummary.flightStatus} />
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Retailer(s):</span>
              {retailerName !== "—" ? (
                <a
                  href="/retailer-management"
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {retailerName}
                </a>
              ) : (
                <span className="font-medium">{retailerName}</span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Cycle(s):</span>
              {campaignSummary?.cycleId ? (
                <a
                  href={`/admin/cycles?cycleId=${campaignSummary.cycleId}`}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {cycleDisplay}
                </a>
              ) : (
                <span className="font-medium">{cycleDisplay}</span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Date Range:</span>
              <span className="font-medium">{dateRange}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Created / Updated:</span>
              <span className="font-medium">
                {campaignSummary.lastUpdateReason === 'REASSIGNMENT' ? (
                  <>
                    {campaignSummary.lastUpdatedByName || "Unknown"}{" "}
                    <span className="text-muted-foreground font-normal">
                      (Reassignment · {formatDate(campaignSummary.lastUpdatedAt)})
                    </span>
                  </>
                ) : (
                  <>
                    {campaignSummary.createdByName || "—"}{" "}
                    <span className="text-muted-foreground font-normal">
                      (Created · {formatDate(campaignSummary.createdAt)})
                    </span>
                  </>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Description:</span>
              <span className="font-medium max-w-[250px] truncate text-right" title={campaignSummary.description || ""}>
                {campaignSummary.description || "None"}
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-sm text-muted-foreground">Saved Snapshot:</span>
              <span className="font-medium max-w-[250px] text-right">
                {snapshotDisplay.value}
                {snapshotDisplay.hint ? (
                  <span className="block text-xs text-muted-foreground font-normal">
                    {snapshotDisplay.hint}
                  </span>
                ) : null}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default CampaignHeader;
